import React, { useEffect, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { Database, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Serialises the data-table config into a single HTML attribute so it survives storage + sanitising.
export const encodeConfig = (cfg) => encodeURIComponent(JSON.stringify(cfg));
export const decodeConfig = (s) => { try { return JSON.parse(decodeURIComponent(s)); } catch { return null; } };
export const describeConfig = (cfg) => cfg ? `Data table: ${cfg.sourceLabel || cfg.source}${cfg.tag ? ` tagged “${cfg.tag}”` : ' (all)'} · ${(cfg.fields || []).length} column${(cfg.fields || []).length === 1 ? '' : 's'}` : 'Data table';

// Atom block node: <div data-datatable="<encoded json>">…</div>. Rendered live by <DataTable> on pages.
export const DataTableNode = Node.create({
  name: 'dataTable',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() { return { config: { default: '', parseHTML: (el) => el.getAttribute('data-datatable') || '', renderHTML: (attrs) => ({ 'data-datatable': attrs.config }) } }; },
  parseHTML() { return [{ tag: 'div[data-datatable]' }]; },
  renderHTML({ HTMLAttributes }) {
    const cfg = decodeConfig(HTMLAttributes['data-datatable'] || '');
    return ['div', mergeAttributes(HTMLAttributes, { class: 'data-table-placeholder', 'data-testid': 'data-table-placeholder' }), describeConfig(cfg)];
  },
  addCommands() {
    return { insertDataTable: (cfg) => ({ commands }) => commands.insertContent({ type: this.name, attrs: { config: encodeConfig(cfg) } }) };
  },
});

// Dialog: choose the data source, a tag to filter by, the columns (checkboxes) and the default sort.
export function DataTableDialog({ onClose, onInsert }) {
  const { dataTableSources } = useAuth();
  const [meta, setMeta] = useState(null);
  const [source, setSource] = useState('members');
  const [tag, setTag] = useState('');
  const [fields, setFields] = useState([]);
  const [sortKey, setSortKey] = useState('');
  const [title, setTitle] = useState('');
  useEffect(() => { dataTableSources().then((m) => { setMeta(m); const s = m.sources[0]; setFields(s.fields.map((f) => f.key)); setSortKey(s.fields[0].key); }).catch(() => setMeta({ sources: [], tags: [] })); }, [dataTableSources]);
  const src = meta && meta.sources.find((s) => s.key === source);
  const pickSource = (k) => { setSource(k); const s = meta.sources.find((x) => x.key === k); setFields(s.fields.map((f) => f.key)); setSortKey(s.fields[0].key); };
  const toggle = (k) => setFields((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]));
  const insert = () => onInsert({ source, sourceLabel: src.label, tag, fields: src.fields.map((f) => f.key).filter((k) => fields.includes(k)), sortKey, title: title.trim() });
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose} data-testid="data-table-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Database size={17} /> Insert a data table</h3>
          <button onClick={onClose} data-testid="data-table-close"><X size={18} /></button>
        </div>
        {!meta ? <div className="p-6"><Loader2 className="animate-spin" size={18} /></div> : (
          <div className="p-5 space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-title" style={{ color: 'var(--navy)' }}>Data source</span>
                <select value={source} onChange={(e) => pickSource(e.target.value)} className="w-full px-2 py-1.5 rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="data-table-source">
                  {meta.sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select></label>
              <label className="block"><span className="text-xs font-title" style={{ color: 'var(--navy)' }}>Only rows tagged</span>
                <select value={tag} onChange={(e) => setTag(e.target.value)} className="w-full px-2 py-1.5 rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="data-table-tag">
                  <option value="">(all rows)</option>
                  {meta.tags.map((t) => <option key={t} value={t}>{t}</option>)}
                </select></label>
            </div>
            <label className="block"><span className="text-xs font-title" style={{ color: 'var(--navy)' }}>Heading (optional)</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Committee contacts" className="w-full px-2 py-1.5 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="data-table-title" /></label>
            <div>
              <span className="text-xs font-title" style={{ color: 'var(--navy)' }}>Columns to show</span>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {src.fields.map((f) => (
                  <label key={f.key} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={fields.includes(f.key)} onChange={() => toggle(f.key)} data-testid={`data-table-field-${f.key}`} /> {f.label}</label>
                ))}
              </div>
            </div>
            <label className="block"><span className="text-xs font-title" style={{ color: 'var(--navy)' }}>Sort by (readers can click any column heading to re-sort)</span>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} className="w-full px-2 py-1.5 rounded border bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }} data-testid="data-table-sort">
                {src.fields.filter((f) => fields.includes(f.key)).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select></label>
            <p className="text-xs" style={{ color: 'var(--olive)' }}>The table fills itself with live data when the page is viewed. Readers can tick rows and click a row to open it.</p>
          </div>
        )}
        <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button onClick={onClose} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
          <button onClick={insert} disabled={!meta || !fields.length} className="px-4 py-2 rounded text-sm font-title disabled:opacity-40" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="data-table-insert">Insert table</button>
        </div>
      </div>
    </div>
  );
}
